/**
 * AP-773 — snapshot / restore of a Cornerstone annotation's `data`.
 *
 * A step stores deep clones of `annotation.data` taken before and after the
 * action. Restoring writes the clone back **in place** (same `data` object —
 * Cornerstone tools hold references) with `cachedStats` intact and
 * `invalidated` cleared, so the repaint draws the restored geometry without
 * recomputing stats and re-firing ANNOTATION_MODIFIED — which would route
 * back through the capture hook as if the reader had dragged again.
 */
import { eventTarget, triggerEvent } from '@cornerstonejs/core';
import { annotation as csAnnotation, Enums } from '@cornerstonejs/tools';
import { store } from '@/state/store';
import { selectSelectedAnnotationUID } from '@/state/slices/measurementSession';
import { getBaseAnnotationStyle } from '@/features/study-detail/Viewer/utils/annotationStyle';
import { triggerAllViewports } from '@/features/study-detail/Viewer/utils/triggerAllViewports';
import { cloneAnnotationValue } from './annotationClone';

export type AnnotationDataSnapshot = Record<string, unknown>;

export function snapshotAnnotationData(
  data: unknown
): AnnotationDataSnapshot | null {
  if (!data || typeof data !== 'object') return null;
  return cloneAnnotationValue(data as AnnotationDataSnapshot);
}

/** `annotationUID → data clone` for every annotation Cornerstone holds. */
export function snapshotAllAnnotations(): Map<string, AnnotationDataSnapshot> {
  const out = new Map<string, AnnotationDataSnapshot>();
  csAnnotation.state.getAllAnnotations().forEach((a) => {
    const clone = snapshotAnnotationData(a.data);
    if (a.annotationUID && clone) out.set(a.annotationUID, clone);
  });
  return out;
}

export function sameAnnotationData(
  a: AnnotationDataSnapshot | null | undefined,
  b: AnnotationDataSnapshot | null | undefined
): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Write a clone back into the live annotation and repaint every pane.
 *
 * The source color (R15) is not part of `data`: adoption writes a
 * per-annotation style override keyed off `data.isAI`, so after the data is
 * back we re-derive that override the same way adoption does. The synthetic
 * ANNOTATION_MODIFIED is what the selected-annotation chip re-renders on;
 * `isApplyingHistory()` keeps the capture hook from reading it as a drag.
 */
export function restoreAnnotationData(
  annotationUID: string,
  snapshot: AnnotationDataSnapshot
): boolean {
  const live = csAnnotation.state.getAnnotation(annotationUID);
  if (!live) return false;
  const data = live.data as AnnotationDataSnapshot;
  Object.keys(data).forEach((k) => delete data[k]);
  Object.assign(data, cloneAnnotationValue(snapshot));
  live.invalidated = false;
  const isSelected =
    selectSelectedAnnotationUID(store.getState()) === annotationUID;
  csAnnotation.config.style.setAnnotationStyles(
    annotationUID,
    getBaseAnnotationStyle(live, { isSelected })
  );
  triggerAllViewports();
  triggerEvent(eventTarget, Enums.Events.ANNOTATION_MODIFIED, {
    annotation: live,
    changeType: 'StatsUpdated',
  });
  return true;
}
